import { Calendar, Layers, Paperclip, Users } from 'lucide-react'
import { motion } from 'framer-motion'
import { Card } from '@/components/ui/card'
import { KanbanCard } from '@/components/kanban'
import type { ProjectBoardDocument, ProjectTaskDocument } from '@/types'

interface Props {
  task: ProjectTaskDocument & { id: string }
  board: ProjectBoardDocument & { id: string }
  onOpen: (taskId: string) => void
}

const PRIORITY_STYLES: Record<'low' | 'medium' | 'high', { className: string; label: string }> = {
  low: { className: 'bg-blue-500/15 text-blue-700', label: 'Low' },
  medium: { className: 'bg-amber-500/15 text-amber-700', label: 'Medium' },
  high: { className: 'bg-red-500/15 text-red-700', label: 'High' },
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function formatDue(dueDate: string): { text: string; className: string } {
  const d = new Date(dueDate + 'T00:00:00')
  if (Number.isNaN(d.getTime())) {
    return { text: dueDate, className: '' }
  }
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffDays = Math.round((d.getTime() - today.getTime()) / 86400000)

  if (diffDays === 0) return { text: 'today', className: 'text-amber-600' }
  if (diffDays === 1) return { text: 'tomorrow', className: '' }
  if (diffDays > 1 && diffDays <= 7) return { text: `in ${diffDays}d`, className: '' }
  if (diffDays === -1) return { text: 'yesterday', className: 'text-red-600' }
  if (diffDays < -1 && diffDays >= -7) {
    return { text: `${Math.abs(diffDays)}d overdue`, className: 'text-red-600' }
  }
  const text = `${MONTHS[d.getMonth()]} ${d.getDate()}`
  return { text, className: diffDays < 0 ? 'text-red-600' : '' }
}

export function TaskCard({ task, board, onOpen }: Props) {
  const labelMap = new Map(
    (board.meta?.labels ?? []).map((l) => [l.id, l] as const)
  )
  const resolvedLabels = (task.labelIds ?? [])
    .map((id) => labelMap.get(id))
    .filter((l): l is { id: string; name: string; color: string } => Boolean(l))
  const visibleLabels = resolvedLabels.slice(0, 3)
  const extraLabelCount = resolvedLabels.length - visibleLabels.length

  const priority = task.priority ? PRIORITY_STYLES[task.priority] : null
  const due = task.dueDate ? formatDue(task.dueDate) : null
  const assigneeCount = task.assigneeIds?.length ?? 0
  const attachmentCount = task.attachments?.length ?? 0
  const hasSubBoard = Boolean(task.childBoardId)
  const hasFooter =
    Boolean(due) || assigneeCount > 0 || attachmentCount > 0 || hasSubBoard

  return (
    <KanbanCard id={task.id}>
      {({ ref, style, attributes, listeners }) => (
        <motion.div
          ref={ref}
          layoutId={`task-card-${task.id}`}
          style={style}
          {...attributes}
          {...listeners}
        >
          <Card
            onClick={() => onOpen(task.id)}
            className="cursor-pointer touch-none overflow-hidden rounded-md py-0 transition-colors hover:bg-accent/40"
          >
            {task.coverMediaUrl && (
              <img
                src={task.coverMediaUrl}
                alt=""
                className="h-24 w-full rounded-t-md object-cover"
              />
            )}

            <div className="flex flex-col gap-2 p-3">
              {visibleLabels.length > 0 && (
                <div className="flex flex-wrap items-center gap-1">
                  {visibleLabels.map((l) => (
                    <span
                      key={l.id}
                      className="rounded px-1.5 py-0.5 text-xs font-medium text-white"
                      style={{ backgroundColor: l.color }}
                      title={l.name}
                    >
                      {l.name}
                    </span>
                  ))}
                  {extraLabelCount > 0 && (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                      +{extraLabelCount}
                    </span>
                  )}
                </div>
              )}

              <p className="truncate text-sm font-medium">{task.title || 'Untitled task'}</p>

              {priority && (
                <div>
                  <span
                    className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${priority.className}`}
                  >
                    {priority.label}
                  </span>
                </div>
              )}

              {hasFooter && (
                <div className="mt-1 flex items-center gap-3 border-t pt-2 text-xs text-muted-foreground">
                  {due && (
                    <span className={`inline-flex items-center gap-1 ${due.className}`}>
                      <Calendar className="h-3 w-3" />
                      {due.text}
                    </span>
                  )}
                  {assigneeCount > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {assigneeCount}
                    </span>
                  )}
                  {attachmentCount > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Paperclip className="h-3 w-3" />
                      {attachmentCount}
                    </span>
                  )}
                  {hasSubBoard && (
                    <Layers className="h-3 w-3" aria-label="Has sub-board" />
                  )}
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      )}
    </KanbanCard>
  )
}
